/** XeAgents.ai**/
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { GitBranch, Loader2, PhoneCall, PhoneOutgoing } from "lucide-react";

interface AgentTestCallDialogProps {
  agent: {
    id: string;
    name: string;
    connectedPhoneNumber?: { phoneNumber: string } | null;
    flow?: { name?: string | null } | null;
  } | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Call my phone so I can hear it", for one agent.
 *
 * The server dials from the number this agent is configured with and runs the
 * agent's own flow and voice - nothing here chooses a number or a flow, which is
 * the point: a test that used a default would not prove the real setup works.
 */
export default function AgentTestCallDialog({ agent, onOpenChange }: AgentTestCallDialogProps) {
  const { toast } = useToast();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");

  const close = (open: boolean) => {
    if (!open) {
      setPhoneNumber("");
      setFirstName("");
    }
    onOpenChange(open);
  };

  const testCall = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/agents/${agent!.id}/test-call`, {
        phoneNumber,
        firstName: firstName.trim() || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Calling you now",
        description: `${agent!.name} is ringing ${phoneNumber.trim()}. It appears in Call Logs once it connects.`,
      });
      close(false);
    },
    onError: (error: any) => {
      toast({ title: "Could not place the test call", description: error.message, variant: "destructive" });
    },
  });

  const from = agent?.connectedPhoneNumber?.phoneNumber;

  return (
    <Dialog open={!!agent} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" /> Test {agent?.name}
          </DialogTitle>
          <DialogDescription>
            The agent calls the number you enter, exactly as it would on a live call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1.5">
            <div className="flex items-center gap-2">
              <PhoneOutgoing className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Calls from</span>
              <span className="font-medium" data-testid="text-test-from-number">
                {from ?? "No number set for this agent"}
              </span>
            </div>
            {agent?.flow?.name && (
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Runs flow</span>
                <span className="font-medium truncate">{agent.flow.name}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="agent-test-first-name">First name</Label>
            <Input
              id="agent-test-first-name"
              placeholder="Alex"
              maxLength={60}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              data-testid="input-agent-test-first-name"
            />
            <p className="text-xs text-muted-foreground">
              The agent uses this wherever the script says the contact's name, such as {"{{first_name}}"}.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="agent-test-number">Number to call</Label>
            <Input
              id="agent-test-number"
              type="tel"
              placeholder="+61 400 000 000"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && phoneNumber.trim() && !testCall.isPending) testCall.mutate();
              }}
              data-testid="input-agent-test-number"
            />
            <p className="text-xs text-muted-foreground">
              Include the country code. A test call uses voice credits like any other call.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          <Button
            onClick={() => testCall.mutate()}
            disabled={!phoneNumber.trim() || !from || testCall.isPending}
            data-testid="button-start-agent-test-call"
          >
            {testCall.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calling...</>
            ) : (
              <><PhoneCall className="h-4 w-4 mr-2" /> Call this number</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
